import _ from 'underscore';

import { LanguageCode, LanguageCodeHelper } from '@biteinc/enums';
import { Strings } from '@biteinc/localization';

import { template } from '../../template';

app.MapFieldRowView = app.FieldGroupView.extend({
  className(...args) {
    const className = app.FieldGroupView.prototype.className.apply(this, args);
    return `${className} map-field-row-view clearfix`;
  },

  render(...args) {
    app.FieldGroupView.prototype.render.apply(this, args);

    this.$name = $('<label class="name"></label>');
    this.$name.html(this.keyModel.displayName());
    this.$el.prepend(this.$name);
    if (this.schema.exclusiveTo) {
      this.$label.prepend(app.renderFrontEndExclusivityTooltip(this.schema.exclusiveTo));
    }
    if (this.schema.tooltip) {
      this.$name.prepend(app.renderTooltip(this.schema.tooltip));
    }

    const $btnGroup = $('<div class="btn-group"></div>');
    this.$deleteButton = $('<button type="button">Delete</button>');
    this.$deleteButton.addClass('btn btn-danger');
    $btnGroup.append(this.$deleteButton);
    this.$el.append($btnGroup);

    this.$deleteButton.prop('disabled', this.isReadOnly);

    return this;
  },
});

// A custom-strings key row. Unlike a plain map row it does not render a fixed field per language;
// it renders a field only for the languages the entry actually has, plus a dropdown to add a
// language and a per-language remove control. A language present here is honored at read time even
// when its value is empty (a deliberate "render nothing"); a language that is absent falls back to
// its localization value. Storage stays flat: the row value is `{ [languageCode]: string }`.
app.CustomStringsRowView = app.MapFieldRowView.extend({
  // Build the row shell only. The per-language fields are built in setValue, once the value (i.e.
  // which languages are present) is known — render runs before setValue.
  render() {
    this.$el.empty();

    this.$name = $('<label class="name"></label>');
    this.$name.html(this.keyModel.displayName());
    this.$el.append(this.$name);
    if (this.schema.tooltip) {
      this.$name.prepend(app.renderTooltip(this.schema.tooltip));
    }

    this.$languageList = $('<div class="language-list"></div>');
    this.$el.append(this.$languageList);

    this._languageDropdown = new app.DropdownView({ isReadOnly: this.isReadOnly });
    this._languageDropdown.render();
    this._languageDropdown.setTitle('Add a Language:');
    this.$el.append(this._languageDropdown.$el);

    const $btnGroup = $('<div class="btn-group"></div>');
    this.$deleteButton = $('<button type="button">Delete</button>');
    this.$deleteButton.addClass('btn btn-danger');
    $btnGroup.append(this.$deleteButton);
    this.$el.append($btnGroup);
    this.$deleteButton.prop('disabled', this.isReadOnly);

    return this;
  },

  setValue(value, model) {
    this.params = {};
    this.model = model;
    this.value = value;
    this.fieldViewsByField = {};
    this.$languageList.empty();

    Object.keys(value || {}).forEach((languageCode) => {
      this._addLanguageField(languageCode, value[languageCode], model);
    });

    // The dropdown lists supported languages + english; `validate` re-runs each time it opens, so a
    // language already added is filtered out until it is removed again.
    const collection = app.AbstractCollection.createFromTsEnum({
      tsEnum: LanguageCode,
      nameGenerator: LanguageCodeHelper.name,
      values: this._supportedLanguages(),
    });
    this._languageDropdown.setup(collection, {
      validate: (languageModel) => !this.fieldViewsByField[languageModel.id],
      onAdd: (languageModel) => {
        // A newly added language starts present-empty: an empty field returns '' (not undefined),
        // so it saves as a deliberate blank rather than being omitted.
        this._addLanguageField(languageModel.id, '', model);
        this.trigger(app.FieldGroupView.Events.FieldGroupDidChangeValue, this);
      },
    });
  },

  _supportedLanguages() {
    const languages = _.chain(this._getSettingsForLevel())
      .map((setting) => setting.get('supportedLanguages'))
      .flatten()
      .uniq()
      .value();
    if (!languages.includes(LanguageCode.EN_US)) {
      languages.push(LanguageCode.EN_US);
    }
    return languages;
  },

  _addLanguageField(languageCode, stringValue, model) {
    const defaultValue = Strings[this.keyModel.id][LanguageCode.EN_US] || '[no default value]';

    const fieldSchema = this.schema.fields[languageCode] || {
      type: 'shortString',
      displayName: LanguageCodeHelper.name(languageCode),
    };
    const FieldViewClass = this._classForField(fieldSchema);
    const fieldView = new FieldViewClass({
      field: languageCode,
      schema: { ...fieldSchema, placeholder: defaultValue },
      subProperty: this.subProperty,
      keyModel: this.keyModel,
      isReadOnly: this.isReadOnly,
      fieldGroupView: this,
    });
    this.fieldViewsByField[languageCode] = fieldView;

    const $languageRow = $('<div class="language-row clearfix"></div>');
    $languageRow.append(fieldView.render().el);
    if (!this.isReadOnly) {
      const $removeButton = $(
        '<button type="button" class="btn btn-danger btn-sm remove-language">Remove</button>',
      );
      $languageRow.append($removeButton);
      $removeButton.click(() => {
        this.stopListening(fieldView);
        fieldView.destroy();
        delete this.fieldViewsByField[languageCode];
        $languageRow.remove();
        this.trigger(app.FieldGroupView.Events.FieldGroupDidChangeValue, this);
      });
    }
    this.$languageList.append($languageRow);

    fieldView.setValue(stringValue || defaultValue, model);
    fieldView.viewWasAddedToDom();

    this.listenTo(fieldView, app.FieldView.Events.FieldDidChangeValue, () => {
      this.trigger(app.FieldGroupView.Events.FieldGroupDidChangeValue, this);
    });
  },
});

app.MapFieldView = app.FieldView.extend({
  className(...args) {
    return `${app.FieldView.prototype.className.apply(this, args)} map-field-view`;
  },
  template: template(
    // prettier-ignore
    '<label class="col-form-label col-md-6"></label>' +
    '<div class="col-md-12 collapsible">' +
      '<div class="wrapper">' +
        '<div class="col-md-12 row-list"></div>' +
      '</div>' +
    '<div>',
  ),

  initialize(...args) {
    app.FieldView.prototype.initialize.apply(this, args);

    this._isCustomStrings = this.field === 'customStrings';
  },

  _addValueRow(rowFields, keyModel, model) {
    const schema = keyModel.get('Schema') || this.schema;
    const tooltip = this.model?.getTooltipForListFieldElement?.(
      this.field,
      keyModel.id,
      this.subProperty,
      keyModel,
    );
    const schemaWithTooltip = {
      ...schema,
      ...(!schema.tooltip && tooltip && { tooltip }),
    };
    let subProperty = this.field;
    if (this.subProperty) {
      subProperty = `${this.subProperty}.${subProperty}`;
    }
    const RowViewClass = this._isCustomStrings ? app.CustomStringsRowView : app.MapFieldRowView;
    const row = new RowViewClass({
      subProperty,
      keyModel,
      schema: schemaWithTooltip,
      isReadOnly: this.isReadOnly,
    });
    this.$rowList.append(row.render().$el);
    setTimeout(() => {
      if (row.viewWasAddedToDom) {
        row.viewWasAddedToDom();
      }
    }, 1);

    row.setValue(rowFields, model);

    if (!this.isPermanent()) {
      row.$deleteButton.click(() => {
        row.remove();
        this.rows = this.rows.filter((existingRow) => {
          return existingRow !== row;
        });
        this.trigger(app.FieldView.Events.FieldDidChangeValue, this);
      });
    }

    this.rows.push(row);
    this.listenTo(row, app.FieldGroupView.Events.FieldGroupDidChangeValue, () => {
      this.trigger(app.FieldView.Events.FieldDidChangeValue, this);
    });
  },

  _getSubtitleForElement(element) {
    if (this.model && this.model.subtitleForListFieldElement) {
      return this.model.subtitleForListFieldElement(this.field, element);
    }
    // empty strings will not show as subtitles
    return '';
  },

  setValue(value, model) {
    this.value = value;
    this.model = model;
    this.initialValue = value;
    const collection = model.getFieldCollection(this.field, this.subProperty, false);

    this.$rowList.html('');
    this.rows = [];
    _.each(this.value || {}, (rowFields, keyId) => {
      // Some maps (e.g. keyString keyed maps) may have arbitrary keys that don't exist in the
      // collection. In that case, create a minimal key model so the UI can render existing values.
      const keyModel = collection.get(keyId) || new app.AbstractModel({ _id: keyId, name: keyId });
      this._addValueRow(rowFields, keyModel, model);
    });

    this.dropdown.setup(collection, {
      getSubtitle: (element) => {
        if (this.model && this.model.subtitleForListFieldElement) {
          return this.model.subtitleForListFieldElement(this.field, element);
        }
        // empty strings will not show as subtitles
        return '';
      },
      validate: (keyModel) => {
        return !_.any(this.rows, (row) => {
          return row.keyModel === keyModel;
        });
      },
      onAdd: (keyModel) => {
        this._addValueRow({}, keyModel, model);
        this.trigger(app.FieldView.Events.FieldDidChangeValue, this);
      },
    });

    this.toggleCollapsedView(!_.size(this.rows));
  },

  getValue() {
    const value = {};
    _.each(this.rows, (row) => {
      const rowValue = row.getValue();
      value[row.options.keyModel.id] = rowValue;
    });
    if (!_.keys(value).length && !this.initialValue) {
      return this.initialValue;
    }

    return value;
  },

  /**
   * @public
   * @returns { { isValid: true } | { isValid: false, invalidFieldNames: string[] } }
   */
  checkValidity() {
    return this.rows.reduce(
      (response, row) => {
        const rowName = row.keyModel.displayName();
        const { isValid, invalidFieldNames } = row.checkValidity();
        if (isValid) {
          return response;
        }
        return {
          isValid: false,
          invalidFieldNames: [
            // previously encountered invalid field names
            ...(response.isValid ? [] : response.invalidFieldNames),
            // Either we have nested fields that are invalid or this current field is invalid by itself.
            ...(invalidFieldNames.length
              ? invalidFieldNames.map((nestedFieldName) => `${rowName} > ${nestedFieldName}`)
              : [rowName]),
          ],
        };
      },
      { isValid: true },
    );
  },

  render() {
    this.$el.html(this.template());

    this.$('.col-form-label').html(`${this.getDisplayName()}:`);

    this.rows = [];
    this.$rowList = this.$('.row-list');

    this.dropdown = new app.DropdownView({ isReadOnly: this.isReadOnly });
    this.dropdown.render();
    this.dropdown.$el.insertAfter(this.$rowList);
    this.dropdown.setTitle(this.schema.key.placeholder || `Select ${this.schema.key.displayName}:`);

    if (this.field === 'customStrings') {
      this.$el.addClass('custom-strings');
    }

    if (this.schema.collapse) {
      this.addCollapseButton();
    }

    return this;
  },
});
